import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Building2 } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, FadeIn, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { getCaseStudy } from "../../data/caseStudies";
import { getSolution } from "../../data/solutions";
import NotFound from "./NotFound";

const Block = ({ no, title, items }) => (
  <div>
    <Reveal>
      <div className="flex items-center gap-4 mb-6">
        <span className="font-marcellus text-nx-gold/60 text-lg">{no}</span>
        <h2 className="font-marcellus text-2xl sm:text-3xl text-white">{title}</h2>
      </div>
    </Reveal>
    <Stagger className="flex flex-col gap-px bg-white/10 border border-white/10">
      {items.map((t, i) => (
        <StaggerItem key={i}>
          <div className="bg-nx-navy px-6 py-5 flex items-start gap-4" data-testid={`case-block-${no}-${i}`}>
            <span className="w-1.5 h-1.5 bg-nx-goldlight rotate-45 mt-2 shrink-0" />
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{t}</p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </div>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const cs = getCaseStudy(slug);
  if (!cs) return <NotFound />;

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid={`project-detail-${slug}`}>
      <SiteHeader />
      <section className="relative bg-nx-navy overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
        <div className="relative nx-wrap pb-14">
          <FadeIn delay={0.15}>
            <Link to="/projects" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors mb-8" data-testid="case-back-link">
              <ArrowLeft size={14} /> All projects
            </Link>
          </FadeIn>
          <div className="grid lg:grid-cols-2 gap-12 items-end">
            <div>
              <FadeIn delay={0.25}>
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{cs.category}</span>
                  {cs.sample && <span className="text-[10px] tracking-[0.22em] uppercase text-slate-400 border border-white/15 px-3 py-1.5" data-testid="case-sample-badge">Representative draft</span>}
                </div>
              </FadeIn>
              <h1 className="font-marcellus text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.12]" data-testid="case-title">{cs.title}</h1>
              <FadeIn delay={0.4}>
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
                  <span className="flex items-center gap-2"><MapPin size={15} className="text-nx-goldlight" /> {cs.location}</span>
                  <span className="flex items-center gap-2"><Building2 size={15} className="text-nx-goldlight" /> {cs.industry}</span>
                </div>
              </FadeIn>
            </div>
            <FadeIn delay={0.35}>
              <div className="nx-clip overflow-hidden border border-white/10">
                <img src={cs.image} alt={cs.title} className="w-full h-56 sm:h-72 object-cover" data-testid="case-image" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-14">
          <div className="flex flex-col gap-14">
            <div>
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-marcellus text-nx-gold/60 text-lg">01</span>
                  <h2 className="font-marcellus text-2xl sm:text-3xl text-white">Client requirement</h2>
                </div>
              </Reveal>
              <Reveal delay={0.1}><p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="case-requirement">{cs.requirement}</p></Reveal>
            </div>
            <div>
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-marcellus text-nx-gold/60 text-lg">02</span>
                  <h2 className="font-marcellus text-2xl sm:text-3xl text-white">The challenge</h2>
                </div>
              </Reveal>
              <Reveal delay={0.1}><p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="case-challenge">{cs.challenge}</p></Reveal>
            </div>
            <Block no="03" title="The design" items={cs.design} />
            <Block no="04" title="Implementation" items={cs.implementation} />
            <Block no="05" title="The result" items={cs.result} />
          </div>
          <div className="lg:pt-2">
            <Reveal delay={0.2}>
              <div className="border border-white/10 bg-white/[0.03] p-7 sticky top-28">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-5">Solutions used</p>
                <div className="flex flex-col gap-3">
                  {cs.solutionsUsed.map((ss) => {
                    const sol = getSolution(ss);
                    return sol ? (
                      <Link key={ss} to={`/solutions/${ss}`} className="group flex items-center justify-between border border-white/10 hover:border-nx-gold/50 px-5 py-3.5 transition-colors" data-testid={`case-solution-${ss}`}>
                        <span className="text-sm text-slate-200">{sol.name}</span>
                        <ArrowRight size={14} className="text-slate-500 group-hover:text-nx-goldlight group-hover:translate-x-1 transition-all" />
                      </Link>
                    ) : null;
                  })}
                </div>
                <Link to="/contact" data-testid="case-cta" className="mt-7 block text-center bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-xs tracking-[0.16em] uppercase px-6 py-4 transition-colors">
                  Contact Us
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
